import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useAuth } from "../auth/AuthContext";
import logo from "../images/logo.png";
import "../login-redesign.css";

/* ------------------------------------------------------------------ *
 * Login (redesign) — replaces src/logInPage.js
 *
 * Same single centred column as the previous page (logo, two fields,
 * remember me, one button). What changed is the treatment: warm canvas,
 * 8px radii, real focus states, a show/hide password toggle, and an
 * inline error block.
 *
 * No new content. No split panel, no marketing copy, no stats.
 * ------------------------------------------------------------------ */

export function LogInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();

  // Land on the group overview after login. A deep link that forced the
  // login (any path other than the root) is still honoured.
  const fromPath = location.state?.from?.pathname;
  const from = fromPath && fromPath !== "/" ? fromPath : "/overview";

  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  // Cosmetic for now — AuthContext.login doesn't take it (see PR notes).
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event) {
    event.preventDefault();
    if (busy) return;

    setError("");
    setBusy(true);
    try {
      const res = await auth.login({ userName, password });
      if (res?.ok) {
        navigate(from, { replace: true });
      } else {
        // Deliberately generic — don't leak which field was wrong.
        setError("Incorrect username or password");
      }
    } catch (err) {
      toast.error("Error with Request ", {
        position: "top-center",
        autoClose: 1000,
        hideProgressBar: true,
        closeOnClick: true,
        draggable: false,
        progress: false,
        theme: "light",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rf-login">
      <form className="rf-login-col" onSubmit={submit}>
        <img className="rf-login-logo" src={logo} alt="Ray's Florist" />

        {error && (
          <div className="rf-login-error" role="alert">
            {error}
          </div>
        )}

        <div className="rf-login-fields">
          <input
            className="rf-input"
            type="text"
            name="username"
            autoComplete="username"
            placeholder="Username"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
          />

          <div className="rf-input-wrap">
            <input
              className="rf-input has-affordance"
              type={showPassword ? "text" : "password"}
              name="password"
              autoComplete="current-password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="rf-input-affordance"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        <div className="rf-login-row">
          <label className="rf-check">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />
            <span className="rf-check-box" aria-hidden="true">
              ✓
            </span>
            <span className="rf-check-label">Remember me</span>
          </label>
          {/* "Forgot password?" omitted until a recovery route exists —
              /forgot-password would only bounce off the catch-all back
              to /login. See the handoff's open question #2. */}
        </div>

        <button type="submit" className="rf-login-submit" disabled={busy}>
          {busy ? "Signing in…" : "Login"}
        </button>

        <div className="rf-login-foot">Ray's Florist · v2.4</div>
      </form>
    </div>
  );
}

export default LogInPage;
